import React, { useMemo, useState, useCallback } from 'react'
import { usePage } from '@inertiajs/react'
import { calculateAllPolos } from '../lib/indexCalculator'
import { exportRawData, exportRankedData } from '../lib/csvExport'
import { canViewPolo, canEditData } from '../lib/permissions'
import Layout from '../components/Layout'
import CorrelationMatrix from '../components/CorrelationMatrix'
import PolosClustering from '../components/PolosClustering'
import Graficos from '../components/Graficos'

const COLORS = {
  connectivity: { bg: '#dc3545', light: '#f8d7da', text: 'white' },
  diversity: { bg: '#0dcaf0', light: '#cff4fc', text: 'white' },
  comfort: { bg: '#198754', light: '#d1e7dd', text: 'white' }
}

const TABLE_COLORS = {
  gray: '#E7E6E6',
  red: '#EA062A',
  orange: '#F3431C',
  connTotal: '#F8D7DA',
  connCell: '#FBEBEC',
  divTotal: '#CFF4FC',
  divCell: '#E7F9FD',
  confTotal: '#D1E7DD',
  confCell: '#E8F3EE'
}

const tooltipStyles = `
  .custom-tooltip {
    position: relative;
    cursor: help;
  }
  .custom-tooltip::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: pre-line;
    max-width: 320px;
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    text-align: left;
    line-height: 1.4;
  }
  .custom-tooltip:hover::after {
    opacity: 1;
    visibility: visible;
  }
  .ranking-scroll {
    flex: 1;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    background: white;
  }
  .ranking-table {
    border-collapse: separate;
    border-spacing: 0;
    min-width: 0;
    width: 100%;
    font-size: 12px;
    table-layout: fixed;
    background: white;
  }
  .ranking-table th,
  .ranking-table td {
    border: 0;
    border-right: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    padding: 5px 4px;
    vertical-align: middle;
    background-clip: padding-box;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }
  .ranking-table tr > *:first-child {
    border-left: 1px solid #dee2e6;
  }
  .ranking-table thead tr:first-child > * {
    border-top: 1px solid #dee2e6;
  }
  .ranking-table th {
    font-weight: 700;
    text-align: center;
  }
  .ranking-table tbody th,
  .ranking-table tbody td {
    text-align: center;
  }
  .ranking-table tbody th:first-child {
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ranking-table tbody td {
    background: white;
  }
  .ranking-table a {
    color: #0d6efd;
    text-decoration: none;
  }
  .ranking-table a:hover {
    text-decoration: underline;
  }
  .ranking-search {
    position: sticky;
    top: 0;
    height: 74px;
    z-index: 30;
    background: ${TABLE_COLORS.orange};
    color: white;
  }
  .ranking-search-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: center;
    gap: 6px 10px;
    min-height: 60px;
    overflow: visible;
    white-space: normal;
  }
  .ranking-search-input {
    flex: 0 1 240px;
    min-width: 150px;
  }
  .ranking-level-options {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    min-width: 0;
    white-space: normal;
  }
  .ranking-group th {
    position: sticky;
    top: 74px;
    height: 38px;
    z-index: 29;
    color: white;
  }
  .ranking-columns th {
    position: sticky;
    top: 112px;
    height: 42px;
    z-index: 28;
    background: white;
  }
  .ranking-table thead th {
    box-shadow: inset 0 -1px 0 #dee2e6;
  }
  .ranking-table col:nth-child(1) { width: 20%; }
  .ranking-table col:nth-child(2) { width: 6%; }
  .ranking-table col:nth-child(3) { width: 8%; }
  .ranking-table col:nth-child(n + 4) { width: 7.333%; }
  .ranking-table tbody tr:nth-child(even) td:not([class*="ranking-bg"]),
  .ranking-table tbody tr:nth-child(even) th:not([class*="ranking-bg"]) {
    background: #f8f9fa;
  }
  .ranking-table .ranking-bg-gray { background: ${TABLE_COLORS.gray}; }
  .ranking-table .ranking-bg-red { background: ${TABLE_COLORS.red}; color: white; }
  .ranking-table .ranking-head-conn { background: ${COLORS.connectivity.bg}; color: white; }
  .ranking-table .ranking-head-div { background: ${COLORS.diversity.bg}; color: white; }
  .ranking-table .ranking-head-conf { background: ${COLORS.comfort.bg}; color: white; }
  .ranking-table .ranking-bg-conn-total { background: ${TABLE_COLORS.connTotal}; }
  .ranking-table .ranking-bg-conn { background: ${TABLE_COLORS.connCell}; }
  .ranking-table .ranking-bg-div-total { background: ${TABLE_COLORS.divTotal}; }
  .ranking-table .ranking-bg-div { background: ${TABLE_COLORS.divCell}; }
  .ranking-table .ranking-bg-conf-total { background: ${TABLE_COLORS.confTotal}; }
  .ranking-table .ranking-bg-conf { background: ${TABLE_COLORS.confCell}; }
  .ranking-table .ranking-bg-conn-total,
  .ranking-table .ranking-bg-div-total,
  .ranking-table .ranking-bg-conf-total {
    font-weight: 700;
  }
`

const Tip = ({ text, children, className = '' }) => (
  <span className={`custom-tooltip ${className}`} data-tooltip={text}>
    {children}
  </span>
)

const deepClone = (obj) => JSON.parse(JSON.stringify(obj))

const pathToTestId = (path) => path.replace(/\./g, '-')

function getConfigValue(config, path) {
  return path.split('.').reduce((current, key) => current?.[key], config)
}

const LEVEL_LABELS = {
  eixo: 'Eixo',
  categoria: 'Categoria',
  indicador: 'Indicador',
  subindicador: 'Subindicador',
  calculo: 'Cálculo'
}

const WEIGHT_TREE = [
  {
    label: 'Conectividade',
    level: 'eixo',
    path: 'connectivity.weight',
    color: COLORS.connectivity.bg,
    children: [
      {
        label: 'Ponto Nodal',
        level: 'categoria',
        path: 'connectivity.components.nodal.weight',
        children: [
          { label: 'Equipamento Urbano', level: 'indicador', path: 'connectivity.components.nodal.indicatorWeights.equipamentoUrbano' },
          { label: 'Terminal/Estação', level: 'indicador', path: 'connectivity.components.nodal.indicatorWeights.terminalTransporte' }
        ]
      },
      {
        label: 'Acessos',
        level: 'categoria',
        path: 'connectivity.components.access.weight',
        children: [
          { label: 'Ônibus', level: 'indicador', path: 'connectivity.components.access.indicatorWeights.onibus' },
          { label: 'Carro', level: 'indicador', path: 'connectivity.components.access.indicatorWeights.estacionamento' },
          { label: 'Bike', level: 'indicador', path: 'connectivity.components.access.indicatorWeights.bicicleta' }
        ]
      }
    ]
  },
  {
    label: 'Diversidade',
    level: 'eixo',
    path: 'diversity.weight',
    color: COLORS.diversity.bg,
    children: [
      {
        label: 'Usos',
        level: 'categoria',
        path: 'diversity.components.uses.weight',
        children: [
          {
            label: 'Uso Edificação',
            level: 'indicador',
            path: 'diversity.components.uses.indicatorWeights.usoEdificacao',
            children: [
              { label: 'Térreo', level: 'subindicador', path: 'diversity.components.uses.buildingUseWeights.terreo' },
              { label: 'Superior', level: 'subindicador', path: 'diversity.components.uses.buildingUseWeights.superior' }
            ]
          },
          { label: 'Horário Noturno', level: 'indicador', path: 'diversity.components.uses.indicatorWeights.horarioNoturno' },
          { label: 'Fim de Semana', level: 'indicador', path: 'diversity.components.uses.indicatorWeights.fimDeSemana' }
        ]
      },
      {
        label: 'Arquitetura',
        level: 'categoria',
        path: 'diversity.components.architecture.weight',
        children: [
          { label: 'Gabarito', level: 'indicador', path: 'diversity.components.architecture.indicatorWeights.gabarito' },
          { label: 'Largura Fachada', level: 'indicador', path: 'diversity.components.architecture.indicatorWeights.testada' },
          { label: 'Idade', level: 'indicador', path: 'diversity.components.architecture.indicatorWeights.idade' }
        ]
      }
    ]
  },
  {
    label: 'Conforto',
    level: 'eixo',
    path: 'comfort.weight',
    color: COLORS.comfort.bg,
    children: [
      {
        label: 'Sensorial',
        level: 'categoria',
        path: 'comfort.components.sensorial.weight',
        children: [
          { label: 'Árvores', level: 'indicador', path: 'comfort.components.sensorial.indicatorWeights.arvores' },
          { label: 'Proteção', level: 'indicador', path: 'comfort.components.sensorial.indicatorWeights.protecao' },
          { label: 'Limpeza', level: 'indicador', path: 'comfort.components.sensorial.indicatorWeights.limpeza' },
          { label: 'Muro', level: 'indicador', path: 'comfort.components.sensorial.indicatorWeights.muros' },
          { label: 'Fachada', level: 'indicador', path: 'comfort.components.sensorial.indicatorWeights.fachada' },
          { label: 'Travessia', level: 'indicador', path: 'comfort.components.sensorial.indicatorWeights.travessia' }
        ]
      },
      {
        label: 'Passeio',
        level: 'categoria',
        path: 'comfort.components.passeio.weight',
        children: [
          { label: 'Largura Calçada', level: 'indicador', path: 'comfort.components.passeio.indicatorWeights.largura' },
          { label: 'Faixa Livre', level: 'indicador', path: 'comfort.components.passeio.indicatorWeights.faixa' },
          { label: 'Piso', level: 'indicador', path: 'comfort.components.passeio.indicatorWeights.piso' },
          { label: 'Conservação Calçada', level: 'indicador', path: 'comfort.components.passeio.indicatorWeights.conservacao' },
          { label: 'Degraus/Barreira', level: 'indicador', path: 'comfort.components.passeio.indicatorWeights.degraus' },
          { label: 'Entrada Veículos', level: 'indicador', path: 'comfort.components.passeio.indicatorWeights.entrada' }
        ]
      }
    ]
  }
]

const CALC_TREE = [
  {
    label: 'Conectividade',
    level: 'eixo',
    color: COLORS.connectivity.bg,
    description: 'Converte acesso físico e transporte em notas de 0, 5 ou 10.',
    children: [
      {
        label: 'Acessos',
        level: 'categoria',
        description: 'Ônibus, carro e bike são combinados depois pelos pesos da aba Pesos.',
        children: [
          {
            label: 'Ônibus',
            level: 'indicador',
            description: 'Corredor, faixa ou ambos recebem nota máxima; sem corredor/faixa recebe zero.',
            controls: [
              { label: 'Corredor', path: 'connectivity.components.access.busScore.corredor', description: 'Nota quando há corredor de ônibus.' },
              { label: 'Faixa', path: 'connectivity.components.access.busScore.faixa', description: 'Nota quando há faixa de ônibus.' },
              { label: 'Ambos', path: 'connectivity.components.access.busScore.ambos', description: 'Nota quando há corredor e faixa.' },
              { label: 'Não', path: 'connectivity.components.access.busScore.nao', description: 'Nota quando não há corredor nem faixa.' }
            ]
          },
          {
            label: 'Carro',
            level: 'indicador',
            description: 'Cada tipo de vaga vira 0, 5 ou 10 pela porcentagem de lotes/trechos com estacionamento; Carro é a média desses tipos.',
            controls: [
              { label: 'Baixo', path: 'connectivity.components.access.parkingThresholds.low', suffix: '%', description: 'Até este percentual, o tipo de estacionamento recebe 0.' },
              { label: 'Alto', path: 'connectivity.components.access.parkingThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ],
            toggles: [
              { label: 'Neutralizar carro em calçadões', path: 'connectivity.components.access.neutralizeCarAccessForPedestrianStreets', description: 'Remove Carro do denominador de Acessos quando o polo for calçadão.' }
            ]
          }
        ]
      }
    ]
  },
  {
    label: 'Diversidade',
    level: 'eixo',
    color: COLORS.diversity.bg,
    description: 'Transforma mistura de usos, horários e características arquitetônicas em notas antes da ponderação.',
    children: [
      {
        label: 'Usos',
        level: 'categoria',
        description: 'Uso Edificação é calculado por Térreo e Superior; Horário Noturno usa o campo após 19h.',
        children: [
          {
            label: 'Uso Edificação',
            level: 'indicador',
            description: 'Média ponderada entre diversidade no térreo e uso residencial/serviços no pavimento superior.',
            children: [
              {
                label: 'Térreo',
                level: 'subindicador',
                controls: [
                  { label: 'Baixo', path: 'diversity.components.uses.terreoBreakpoints.low', suffix: 'usos', description: 'Até esta quantidade de atividades distintas, recebe 0.' },
                  { label: 'Alto', path: 'diversity.components.uses.terreoBreakpoints.high', suffix: 'usos', description: 'Acima desta quantidade, recebe 10; entre baixo e alto recebe 5.' }
                ]
              },
              {
                label: 'Superior',
                level: 'subindicador',
                controls: [
                  { label: 'Baixo', path: 'diversity.components.uses.superiorThresholds.low', suffix: '%', description: 'Até este percentual de residencial/serviços no superior, recebe 0.' },
                  { label: 'Alto', path: 'diversity.components.uses.superiorThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
                ]
              }
            ]
          },
          {
            label: 'Horário Noturno',
            level: 'indicador',
            description: 'Usa a porcentagem de varejos com atividade após 19h.',
            controls: [
              { label: 'Baixo', path: 'diversity.components.uses.noturnoThresholds.low', suffix: '%', description: 'Até este percentual, recebe 0.' },
              { label: 'Alto', path: 'diversity.components.uses.noturnoThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Fim de Semana',
            level: 'indicador',
            description: 'Usa a porcentagem de varejos que funcionam no fim de semana.',
            controls: [
              { label: 'Baixo', path: 'diversity.components.uses.fdsThresholds.low', suffix: '%', description: 'Até este percentual, recebe 0.' },
              { label: 'Alto', path: 'diversity.components.uses.fdsThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          }
        ]
      },
      {
        label: 'Arquitetura',
        level: 'categoria',
        description: 'Gabarito, fachada/testada e idade são convertidos em notas e depois têm pesos iguais inicialmente.',
        children: [
          {
            label: 'Gabarito',
            level: 'indicador',
            description: 'Favorece predominância de gabarito baixo, dá nota intermediária para predominância média e penaliza predominância alta.',
            controls: [
              { label: '>14 penaliza', path: 'diversity.components.architecture.gabaritoThresholds.tallHigh', suffix: '%', description: 'Acima deste percentual de edifícios altos, a nota é 0.' },
              { label: 'Médio baixo', path: 'diversity.components.architecture.gabaritoThresholds.midLow', suffix: '%', description: 'Início da faixa de predominância média para nota 5.' },
              { label: 'Médio alto', path: 'diversity.components.architecture.gabaritoThresholds.midHigh', suffix: '%', description: 'Fim da faixa de predominância média para nota 5.' },
              { label: 'Baixo premia', path: 'diversity.components.architecture.gabaritoThresholds.lowRiseLow', suffix: '%', description: 'Acima deste percentual de gabarito baixo, a nota é 10.' }
            ]
          },
          {
            label: 'Largura Fachada',
            level: 'indicador',
            description: 'Quanto maior a predominância de testadas largas, menor a nota.',
            controls: [
              { label: 'Larga penaliza', path: 'diversity.components.architecture.testadaThresholds.large', suffix: '%', description: 'A partir deste percentual de testada larga, recebe 0.' },
              { label: 'Larga média', path: 'diversity.components.architecture.testadaThresholds.medium', suffix: '%', description: 'A partir deste percentual, recebe 5; abaixo dele recebe 10.' }
            ]
          },
          {
            label: 'Idade',
            level: 'indicador',
            description: 'Pontua pela diversidade de faixas etárias dos imóveis observados.',
            controls: [
              { label: 'Jovem', path: 'diversity.components.architecture.idadeBreakpoints.young', suffix: 'anos', description: 'Limite superior da faixa jovem.' },
              { label: 'Antiga', path: 'diversity.components.architecture.idadeBreakpoints.old', suffix: 'anos', description: 'Acima deste limite entra na faixa antiga; duas faixas recebem 5 e três faixas recebem 10.' }
            ]
          }
        ]
      }
    ]
  },
  {
    label: 'Conforto',
    level: 'eixo',
    color: COLORS.comfort.bg,
    description: 'Converte qualidades ambientais e de passeio em notas de conforto.',
    children: [
      {
        label: 'Sensorial',
        level: 'categoria',
        description: 'Indicadores de ambiência urbana. Em geral, acima do limite alto recebe 10 e entre limites recebe 5.',
        children: [
          {
            label: 'Árvores',
            level: 'indicador',
            controls: [
              { label: 'Baixo', path: 'comfort.components.sensorial.arborizacaoThresholds.low', suffix: '%', description: 'Até este percentual com arborização, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.sensorial.arborizacaoThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Proteção',
            level: 'indicador',
            controls: [
              { label: 'Baixo', path: 'comfort.components.sensorial.protecaoThresholds.low', suffix: '%', description: 'Até este percentual com proteção de sol/chuva, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.sensorial.protecaoThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Limpeza',
            level: 'indicador',
            description: 'Usa a porcentagem de trechos com limpeza ótima.',
            controls: [
              { label: 'Baixo', path: 'comfort.components.sensorial.limpezaThresholds.low', suffix: '%', description: 'Até este percentual de limpeza ótima, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.sensorial.limpezaThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Muro',
            level: 'indicador',
            description: 'Indicador invertido: menos muros é melhor.',
            controls: [
              { label: 'Baixo', path: 'comfort.components.sensorial.murosThresholds.low', suffix: '%', description: 'Até este percentual de muros, recebe 10.' },
              { label: 'Alto', path: 'comfort.components.sensorial.murosThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 0; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Fachada',
            level: 'indicador',
            controls: [
              { label: 'Baixo', path: 'comfort.components.sensorial.fachadaThresholds.low', suffix: '%', description: 'Até este percentual de fachada conservada/ativa, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.sensorial.fachadaThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Travessia',
            level: 'indicador',
            children: [
              {
                label: 'Semáforos',
                level: 'subindicador',
                controls: [
                  { label: 'Baixo', path: 'comfort.components.sensorial.semaforosThresholds.low', suffix: '%', description: 'Até este percentual com semáforo, recebe 0.' },
                  { label: 'Alto', path: 'comfort.components.sensorial.semaforosThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
                ]
              },
              {
                label: 'Faixa de Pedestre',
                level: 'subindicador',
                controls: [
                  { label: 'Baixo', path: 'comfort.components.sensorial.faixaThresholds.low', suffix: '%', description: 'Até este percentual com faixa, recebe 0.' },
                  { label: 'Alto', path: 'comfort.components.sensorial.faixaThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
                ]
              }
            ]
          }
        ]
      },
      {
        label: 'Passeio',
        level: 'categoria',
        description: 'Indicadores de calçada e circulação local.',
        children: [
          {
            label: 'Largura Calçada',
            level: 'indicador',
            description: 'Usa a predominância da faixa de largura mais favorável.',
            controls: [
              { label: 'Predominância', path: 'comfort.components.passeio.larguraThreshold', suffix: '%', description: 'Percentual mínimo de calçadas largas para nota máxima.' }
            ]
          },
          {
            label: 'Faixa Livre',
            level: 'indicador',
            controls: [
              { label: 'Baixo', path: 'comfort.components.passeio.faixaThresholds.low', suffix: '%', description: 'Até este percentual com faixa livre, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.passeio.faixaThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Piso',
            level: 'indicador',
            controls: [
              { label: 'Baixo', path: 'comfort.components.passeio.guiasThresholds.low', suffix: '%', description: 'Até este percentual com piso/rebaixo acessível, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.passeio.guiasThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Conservação Calçada',
            level: 'indicador',
            controls: [
              { label: 'Baixo', path: 'comfort.components.passeio.calcadaThresholds.low', suffix: '%', description: 'Até este percentual de calçada ótima, recebe 0.' },
              { label: 'Alto', path: 'comfort.components.passeio.calcadaThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 10; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Degraus/Barreira',
            level: 'indicador',
            description: 'Indicador invertido: menos barreiras é melhor.',
            controls: [
              { label: 'Baixo', path: 'comfort.components.passeio.degrausThresholds.low', suffix: '%', description: 'Até este percentual com degraus/barreiras, recebe 10.' },
              { label: 'Alto', path: 'comfort.components.passeio.degrausThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 0; entre baixo e alto recebe 5.' }
            ]
          },
          {
            label: 'Entrada Veículos',
            level: 'indicador',
            description: 'Indicador invertido: menos entradas de veículos melhora a experiência de passeio.',
            controls: [
              { label: 'Baixo', path: 'comfort.components.passeio.entradasThresholds.low', suffix: '%', description: 'Até este percentual com entrada de veículos, recebe 10.' },
              { label: 'Alto', path: 'comfort.components.passeio.entradasThresholds.high', suffix: '%', description: 'Acima deste percentual, recebe 0; entre baixo e alto recebe 5.' }
            ]
          }
        ]
      }
    ]
  }
]

function ConfigInput({ config, item, onChange, type = 'number', compact = false }) {
  const value = getConfigValue(config, item.path)
  const control = (
    <div className={`flex ${compact ? 'w-24' : 'w-28'}`}>
      <input
        type={type}
        className={`w-full px-2 py-1 text-sm border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 ${item.suffix ? 'rounded-l-md' : 'rounded-md'}`}
        value={value ?? 0}
        min={item.min ?? 0}
        max={item.max ?? 100}
        step={item.step ?? 0.5}
        data-testid={`config-input-${pathToTestId(item.path)}`}
        onChange={(e) => {
          const nextValue = Number(e.target.value)
          onChange(item.path, Number.isFinite(nextValue) ? nextValue : 0)
        }}
      />
      {item.suffix && (
        <span className="inline-flex min-w-10 items-center justify-center px-2 py-1 text-xs text-gray-500 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md">
          {item.suffix}
        </span>
      )}
      {!item.suffix && <span className="sr-only">valor</span>}
    </div>
  )

  if (compact) return control

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="flex-1 text-gray-700">
        <span className="block">{item.label}</span>
        {item.description && (
          <span className="block text-xs leading-snug text-gray-500">
            {item.description}
          </span>
        )}
      </span>
      {control}
    </label>
  )
}

function ConfigToggle({ config, item, onChange }) {
  const value = Boolean(getConfigValue(config, item.path))

  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-gray-700">
        <span className="block">{item.label}</span>
        {item.description && (
          <span className="block text-xs leading-snug text-gray-500">
            {item.description}
          </span>
        )}
      </span>
      <input
        type="checkbox"
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
        checked={value}
        data-testid={`config-toggle-${pathToTestId(item.path)}`}
        onChange={(e) => onChange(item.path, e.target.checked)}
      />
    </label>
  )
}

function ConfigTreeNode({ node, config, onChange, mode, depth = 0, inheritedColor }) {
  const color = node.color || inheritedColor || '#9ca3af'
  const children = node.children || []
  const controls = node.controls || []
  const toggles = node.toggles || []
  const isWeightMode = mode === 'pesos'
  const isTopLevel = depth === 0
  const labelClass = node.level === 'eixo'
    ? 'font-bold text-gray-900'
    : node.level === 'categoria'
      ? 'font-semibold text-gray-800'
      : 'font-medium text-gray-700'
  const inputItem = node.path
    ? {
        min: 0,
        max: isWeightMode ? 20 : 100,
        step: isWeightMode ? 0.5 : 1,
        suffix: isWeightMode ? 'peso' : node.suffix,
        ...node
      }
    : null

  return (
    <div
      className={isTopLevel ? 'bg-white rounded-lg shadow-sm mb-3 border-0 p-3' : 'pl-3 border-l'}
      style={isTopLevel ? { borderTop: `3px solid ${color}` } : { borderColor: `${color}55` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="inline-block rounded-full flex-shrink-0"
              style={{ width: node.level === 'eixo' ? '8px' : '6px', height: node.level === 'eixo' ? '8px' : '6px', backgroundColor: color }}
            />
            <span className="text-[10px] uppercase tracking-wide text-gray-400">
              {LEVEL_LABELS[node.level]}
            </span>
          </div>
          <div className={`mt-0.5 text-sm leading-tight break-words ${labelClass}`}>
            {node.label}
          </div>
          {node.description && (
            <div className="mt-1 text-xs leading-snug text-gray-500">
              {node.description}
            </div>
          )}
        </div>
        {inputItem && (
          <ConfigInput
            config={config}
            item={inputItem}
            onChange={onChange}
            compact
          />
        )}
      </div>

      {(controls.length > 0 || toggles.length > 0) && (
        <div className="mt-2 space-y-2">
          {controls.map(item => (
            <ConfigInput
              key={item.path}
              config={config}
              item={{ min: 0, max: 100, step: 1, ...item }}
              onChange={onChange}
            />
          ))}
          {toggles.map(item => (
            <ConfigToggle key={item.path} config={config} item={item} onChange={onChange} />
          ))}
        </div>
      )}

      {children.length > 0 && (
        <div className={isTopLevel ? 'mt-3 space-y-3' : 'mt-2 space-y-2'}>
          {children.map(child => (
            <ConfigTreeNode
              key={`${node.label}-${child.label}`}
              node={child}
              config={config}
              onChange={onChange}
              mode={mode}
              depth={depth + 1}
              inheritedColor={color}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ConfigTree({ tree, config, onChange, mode }) {
  return (
    <>
      {tree.map(node => (
        <ConfigTreeNode
          key={node.label}
          node={node}
          config={config}
          onChange={onChange}
          mode={mode}
        />
      ))}
    </>
  )
}

function ConfigPanel({ config, setConfig, isModified, onReset }) {
  const [mode, setMode] = useState('pesos')

  const updateConfig = useCallback((path, value) => {
    setConfig(prev => {
      const next = deepClone(prev)
      const keys = path.split('.')
      let current = next

      for (let index = 0; index < keys.length - 1; index += 1) {
        current = current[keys[index]]
      }

      current[keys[keys.length - 1]] = value
      return next
    })
  }, [setConfig])

  const tree = mode === 'pesos' ? WEIGHT_TREE : CALC_TREE

  return (
    <div className="bg-gray-100 rounded-xl p-3" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }} data-testid="iqup-config-panel">
      <div className="bg-white rounded-lg shadow-sm mb-3 border-0 p-3">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h6 className="mb-0 font-bold">Configuração IQUP</h6>
          <button
            type="button"
            className="px-2 py-1 text-sm border border-gray-400 rounded hover:bg-gray-100 transition-colors"
            onClick={onReset}
            disabled={!isModified}
            data-testid="reset-config"
            title="Restaurar configuração oficial"
          >
            <i className="fa fa-undo" />
            <span className="sr-only">Restaurar oficial</span>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-1 mb-3">
          <button
            type="button"
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${mode === 'pesos' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setMode('pesos')}
            data-testid="config-mode-pesos"
          >
            <i className="fa fa-balance-scale mr-1" />
            Pesos
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${mode === 'calculos' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setMode('calculos')}
            data-testid="config-mode-calculos"
          >
            <i className="fa fa-calculator mr-1" />
            Cálculos
          </button>
        </div>

        <div className="text-sm font-medium text-gray-700">
          {mode === 'pesos'
            ? 'Pesos combinam notas já calculadas: IQUP = média ponderada dos eixos.'
            : 'Cálculos transformam dados brutos em notas 0, 5 ou 10 antes dos pesos.'}
        </div>
        {mode === 'calculos' && (
          <div className="mt-1 text-xs leading-snug text-gray-500">
            Como regra geral: até o limite baixo recebe 0, entre baixo e alto recebe 5, acima do alto recebe 10. Indicadores marcados como invertidos fazem o oposto.
          </div>
        )}
        {mode === 'pesos' && (
          <div className="mt-1 text-xs leading-snug text-gray-500">
            Térreo e Superior são subindicadores de Uso Edificação, não pares de Horário Noturno e Fim de Semana.
          </div>
        )}
        <div className="mt-2 text-xs font-medium text-gray-600">
          IQUP = (Conectividade + Diversidade + Conforto) / 3
        </div>
      </div>

      <ConfigTree tree={tree} config={config} onChange={updateConfig} mode={mode} />
    </div>
  )
}

function RankingPoloRow({ polo, canView }) {
  const { Conectividade, Diversidade, Conforto } = polo.indicadores.Componentes

  return (
    <tr>
      <th className="ranking-bg-gray">
        {canView ? (
          <a href={`/polos/${polo.id}`}>{polo.nome}</a>
        ) : (
          <span title="Solicite autorização para visualizar este polo">{polo.nome}</span>
        )}
      </th>
      <td className="ranking-bg-red">{polo.nivel}</td>
      <td>{polo.indicadores.Valor.toFixed(2)}</td>
      <td className="ranking-bg-conn-total">{Conectividade.Valor.toFixed(2)}</td>
      <td className="ranking-bg-conn">{Conectividade.Componentes['Ponto Nodal'].toFixed(2)}</td>
      <td className="ranking-bg-conn">{Conectividade.Componentes.Acessos.Valor.toFixed(2)}</td>
      <td className="ranking-bg-div-total">{Diversidade.Valor.toFixed(2)}</td>
      <td className="ranking-bg-div">{Diversidade.Componentes.Arquitetura.Valor.toFixed(2)}</td>
      <td className="ranking-bg-div">{Diversidade.Componentes.Usos.Valor.toFixed(2)}</td>
      <td className="ranking-bg-conf-total">{Conforto.Valor.toFixed(2)}</td>
      <td className="ranking-bg-conf">{Conforto.Componentes.Sensorial.Valor.toFixed(2)}</td>
      <td className="ranking-bg-conf">{Conforto.Componentes.Passeio.Valor.toFixed(2)}</td>
    </tr>
  )
}

function RankingTable({
  polos,
  canView,
  searchTerm,
  setSearchTerm,
  nivelFilter,
  setNivelFilter,
  handleSort,
  sortField,
  sortOrder
}) {
  const sortIcon = (field) => {
    if (sortField === field) {
      return <i className={`fa fa-fw fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} />
    }

    return <i className="fa fa-fw fa-sort" />
  }
  const toggleLevel = (nivel) => {
    setNivelFilter(prev => {
      const next = new Set(prev)
      if (next.has(nivel)) {
        next.delete(nivel)
      } else {
        next.add(nivel)
      }
      return next
    })
  }

  return (
    <>
      <div className="ranking-scroll">
        <table className="ranking-table" data-testid="ranking-table">
          <colgroup>
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th colSpan="12" className="ranking-search">
                <div className="ranking-search-content text-left">
                  <label className="font-bold mb-0" htmlFor="ranking-search">Pesquise o nome do polo</label>
                  <input
                    type="text"
                    className="ranking-search-input px-3 py-1 text-sm text-gray-900 border border-gray-300 rounded"
                    id="ranking-search"
                    placeholder="Digite o nome do polo"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="ranking-level-options">
                    <span className="font-bold">Escolha os níveis</span>
                    {[1, 2, 3].map(nivel => (
                      <label key={nivel} className="inline-flex items-center gap-1 mb-0">
                        <input
                          type="checkbox"
                          checked={nivelFilter.has(nivel)}
                          onChange={() => toggleLevel(nivel)}
                        />
                        <span>Nível {nivel}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </th>
            </tr>
            <tr className="ranking-group">
              <th colSpan="3" className="ranking-bg-gray"></th>
              <th colSpan="3" className="ranking-head-conn" onClick={() => handleSort('conectividade')}>
                Conectividade {sortIcon('conectividade')}
              </th>
              <th colSpan="3" className="ranking-head-div" onClick={() => handleSort('diversidade')}>
                Diversidade {sortIcon('diversidade')}
              </th>
              <th colSpan="3" className="ranking-head-conf" onClick={() => handleSort('conforto')}>
                Conforto {sortIcon('conforto')}
              </th>
            </tr>
            <tr className="ranking-columns">
              <th className="ranking-bg-gray" onClick={() => handleSort('nome')}>Nome do Polo {sortIcon('nome')}</th>
              <th className="ranking-bg-red" onClick={() => handleSort('nivel')}>Nível {sortIcon('nivel')}</th>
              <th onClick={() => handleSort('valor')}>Nota IQUP {sortIcon('valor')}</th>
              <th className="ranking-bg-conn-total" onClick={() => handleSort('conectividade')}>Total {sortIcon('conectividade')}</th>
              <th className="ranking-bg-conn" onClick={() => handleSort('pontoNodal')}>Pontos Nodais {sortIcon('pontoNodal')}</th>
              <th className="ranking-bg-conn" onClick={() => handleSort('acessos')}>Acessos {sortIcon('acessos')}</th>
              <th className="ranking-bg-div-total" onClick={() => handleSort('diversidade')}>Total {sortIcon('diversidade')}</th>
              <th className="ranking-bg-div" onClick={() => handleSort('arquitetura')}>Arquitetura {sortIcon('arquitetura')}</th>
              <th className="ranking-bg-div" onClick={() => handleSort('usos')}>Usos {sortIcon('usos')}</th>
              <th className="ranking-bg-conf-total" onClick={() => handleSort('conforto')}>Total {sortIcon('conforto')}</th>
              <th className="ranking-bg-conf" onClick={() => handleSort('sensorial')}>Sensorial {sortIcon('sensorial')}</th>
              <th className="ranking-bg-conf" onClick={() => handleSort('passeio')}>Calçadas {sortIcon('passeio')}</th>
            </tr>
          </thead>
          <tbody>
            {polos.map((polo) => (
              <RankingPoloRow key={polo.id} polo={polo} canView={canView} />
            ))}
          </tbody>
        </table>
      </div>
      {polos.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <i className="fa fa-search fa-3x mb-3 opacity-25" />
          <p>Nenhum polo encontrado.</p>
        </div>
      )}
    </>
  )
}

export default function Ranking({
  polos: initialPolos,
  default_config: defaultConfig
}) {
  const { auth } = usePage().props
  const canView = canViewPolo(auth?.user)
  const canConfigure = canEditData(auth?.user)
  const [config, setConfig] = useState(() => deepClone(defaultConfig))
  const [searchTerm, setSearchTerm] = useState('')
  const [nivelFilter, setNivelFilter] = useState(new Set([1, 2]))
  const [sortField, setSortField] = useState('valor')
  const [sortOrder, setSortOrder] = useState('desc')
  const [activeTab, setActiveTab] = useState('ranking')
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)

  const isConfigModified = useMemo(() => JSON.stringify(config) !== JSON.stringify(defaultConfig), [config, defaultConfig])
  const resetConfig = useCallback(() => setConfig(deepClone(defaultConfig)), [defaultConfig])

  const toggleNivel = (nivel) => {
    setNivelFilter(prev => {
      const next = new Set(prev)
      if (next.has(nivel)) {
        next.delete(nivel)
      } else {
        next.add(nivel)
      }
      return next
    })
  }

  const calculatedPolos = useMemo(() => calculateAllPolos(initialPolos, config), [initialPolos, config])

  const filteredPolos = useMemo(() => {
    let result = calculatedPolos

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim()
      result = result.filter(p => p.nome.toLowerCase().includes(term))
    }

    if (nivelFilter.size > 0) {
      result = result.filter(p => nivelFilter.has(p.nivel))
    }

    result = [...result].sort((a, b) => {
      let aVal
      let bVal

      switch (sortField) {
        case 'nome':
          return sortOrder === 'asc' ? a.nome.localeCompare(b.nome) : b.nome.localeCompare(a.nome)
        case 'nivel':
          aVal = a.nivel
          bVal = b.nivel
          break
        case 'valor':
          aVal = a.indicadores.Valor
          bVal = b.indicadores.Valor
          break
        case 'conectividade':
          aVal = a.indicadores.Componentes.Conectividade.Valor
          bVal = b.indicadores.Componentes.Conectividade.Valor
          break
        case 'pontoNodal':
          aVal = a.indicadores.Componentes.Conectividade.Componentes['Ponto Nodal']
          bVal = b.indicadores.Componentes.Conectividade.Componentes['Ponto Nodal']
          break
        case 'acessos':
          aVal = a.indicadores.Componentes.Conectividade.Componentes.Acessos.Valor
          bVal = b.indicadores.Componentes.Conectividade.Componentes.Acessos.Valor
          break
        case 'diversidade':
          aVal = a.indicadores.Componentes.Diversidade.Valor
          bVal = b.indicadores.Componentes.Diversidade.Valor
          break
        case 'usos':
          aVal = a.indicadores.Componentes.Diversidade.Componentes.Usos.Valor
          bVal = b.indicadores.Componentes.Diversidade.Componentes.Usos.Valor
          break
        case 'arquitetura':
          aVal = a.indicadores.Componentes.Diversidade.Componentes.Arquitetura.Valor
          bVal = b.indicadores.Componentes.Diversidade.Componentes.Arquitetura.Valor
          break
        case 'conforto':
          aVal = a.indicadores.Componentes.Conforto.Valor
          bVal = b.indicadores.Componentes.Conforto.Valor
          break
        case 'sensorial':
          aVal = a.indicadores.Componentes.Conforto.Componentes.Sensorial.Valor
          bVal = b.indicadores.Componentes.Conforto.Componentes.Sensorial.Valor
          break
        case 'passeio':
          aVal = a.indicadores.Componentes.Conforto.Componentes.Passeio.Valor
          bVal = b.indicadores.Componentes.Conforto.Componentes.Passeio.Valor
          break
        default:
          aVal = a.indicadores.Valor
          bVal = b.indicadores.Valor
      }

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal
    })

    return result
  }, [calculatedPolos, searchTerm, nivelFilter, sortField, sortOrder])

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }, [sortField, sortOrder])

  return (
    <Layout fullWidth>
      <div className="w-full px-4 py-4">
        <style>{tooltipStyles}</style>

        <div className="flex flex-wrap mb-4">
          <div className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold mb-0">IQUP</h1>
                <Tip text="Estrutura hierárquica do IQUP.">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isConfigModified ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                    <i className={`fa ${isConfigModified ? 'fa-sliders' : 'fa-check'} mr-1`} />
                    {isConfigModified ? 'Cenário customizado' : 'Modelo Oficial'}
                  </span>
                </Tip>
              </div>
              <ul className="flex flex-wrap gap-1">
                <li><button className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'ranking' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveTab('ranking')}><i className="fa fa-list-ol mr-1" />Ranking</button></li>
                <li><button className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'graficos' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveTab('graficos')}><i className="fa fa-bar-chart mr-1" />Gráficos</button></li>
                <li><button className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'correlation' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveTab('correlation')}><i className="fa fa-th mr-1" />Correlações</button></li>
                <li><button className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'clustering' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveTab('clustering')}><i className="fa fa-object-group mr-1" />Agrupamentos</button></li>
              </ul>
            </div>
            <p className="text-gray-500 mb-0 text-sm mt-1">
              {activeTab === 'ranking'
                ? 'Ranking IQUP com pesos e cálculos configuráveis'
                : activeTab === 'graficos'
                  ? 'Visualizações derivadas do cálculo IQUP'
                  : activeTab === 'correlation'
                    ? 'Matriz de correlação entre as subdimensões do IQUP'
                    : 'Agrupamento de polos por características agregadas'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap -mx-2">
          {canConfigure && (
            <div className="w-full lg:w-1/4 md:w-1/3 px-2 mb-4">
              <div style={{ position: 'sticky', top: '1rem' }}>
                <ConfigPanel
                  config={config}
                  setConfig={setConfig}
                  isModified={isConfigModified}
                  onReset={resetConfig}
                />
              </div>
            </div>
          )}

          <div className={`px-2 w-full ${canConfigure ? 'lg:w-3/4 md:w-2/3' : ''}`}>
            <div className="bg-white rounded-lg shadow-sm flex flex-col border-0" style={{ position: 'sticky', top: '1rem', maxHeight: 'calc(100vh - 2rem)' }}>
              <div className="p-2 border-b flex-shrink-0">
                <div className="flex flex-wrap gap-2 items-center">
                  {activeTab !== 'ranking' && (
                    <div className="w-full md:w-1/3 text-sm text-gray-500">
                      <i className="fa fa-info-circle mr-1" />
                      {activeTab === 'graficos' && 'Gráficos baseados no cálculo IQUP'}
                      {activeTab === 'correlation' && 'Correlações entre subdimensões do IQUP'}
                      {activeTab === 'clustering' && 'Agrupamentos sobre dados agregados'}
                    </div>
                  )}

                  {activeTab !== 'ranking' && (
                    <div className="flex items-center gap-3">
                      {[1, 2, 3].map(nivel => (
                        <label key={nivel} className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            checked={nivelFilter.has(nivel)}
                            onChange={() => toggleNivel(nivel)}
                          />
                          <span className="ml-1 text-sm">Nível {nivel}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div>
                    <div className="relative" onMouseLeave={() => setExportDropdownOpen(false)}>
                      <button className="px-3 py-1 text-sm border border-gray-400 rounded hover:bg-gray-100 transition-colors" type="button" onClick={() => setExportDropdownOpen(!exportDropdownOpen)}>
                        <i className="fa fa-download mr-1" />
                        Exportar CSV
                        <i className="fa fa-caret-down ml-1" />
                      </button>
                      {exportDropdownOpen && (
                        <ul className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
                          <li>
                            <button className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors" onClick={() => {
                              exportRankedData(filteredPolos, nivelFilter)
                              setExportDropdownOpen(false)
                            }}>
                              <i className="fa fa-list-ol mr-2 text-blue-600" />
                              Ranking IQUP
                              <small className="block text-gray-500">Pontuação e subdimensões do IQUP</small>
                            </button>
                          </li>
                          <li>
                            <button className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors" onClick={() => {
                              exportRawData(initialPolos, nivelFilter)
                              setExportDropdownOpen(false)
                            }}>
                              <i className="fa fa-database mr-2 text-green-600" />
                              Dados Agregados
                              <small className="block text-gray-500">Base agregada para análises auxiliares</small>
                            </button>
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <small className="text-gray-500">{filteredPolos.length} polos</small>
                  </div>
                </div>
              </div>

              {activeTab === 'ranking' ? (
                <RankingTable
                  polos={filteredPolos}
                  canView={canView}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  nivelFilter={nivelFilter}
                  setNivelFilter={setNivelFilter}
                  handleSort={handleSort}
                  sortField={sortField}
                  sortOrder={sortOrder}
                />
              ) : activeTab === 'graficos' ? (
                <div className="p-4" style={{ overflow: 'auto', flex: 1 }}>
                  <Graficos calculatedPolos={calculatedPolos} nivelFilter={nivelFilter} config={config} />
                </div>
              ) : activeTab === 'correlation' ? (
                <div className="p-4" style={{ overflow: 'auto', flex: 1 }}>
                  <CorrelationMatrix calculatedPolos={calculatedPolos} nivelFilter={nivelFilter} />
                </div>
              ) : (
                <div className="p-4" style={{ overflow: 'auto', flex: 1 }}>
                  <PolosClustering polos={initialPolos} nivelFilter={nivelFilter} canView={canView} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap mt-3">
          <div className="w-full">
            <div className="bg-gray-50 border border-gray-200 rounded px-4 py-3 text-sm">
              <i className="fa fa-info-circle mr-2 text-blue-500" />
              {activeTab === 'ranking' ? (
                <>
                  <strong>{isConfigModified ? 'Cenário customizado:' : 'Estrutura padrão:'}</strong> Esta tela reflete a hierarquia de eixos, categorias e indicadores do IQUP.
                </>
              ) : activeTab === 'graficos' ? (
                <>
                  <strong>Gráficos:</strong> Visualizações do IQUP e dos três eixos principais.
                </>
              ) : activeTab === 'correlation' ? (
                <>
                  <strong>Matriz de Correlação:</strong> Relações entre as subdimensões do IQUP.
                </>
              ) : (
                <>
                  <strong>Agrupamentos:</strong> Clustering baseado em características agregadas dos polos.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
